import { AsyncPipe, CurrencyPipe, NgFor, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Price, PRICE_TYPE } from '../../../../../../models/price';
import { HlCurrencyPipe } from '../../../../../../shared/pipes/hl-currency.pipe';
import { OrdinalPipe } from '../../../../../../shared/pipes/ordinal.pipe';

@Component({
  selector: 'prices-display',
  template: `
    <div
      class="flex flex-col justify-start items-stretch gap-3 p-4 bg-white rounded-lg shadow-sm border border-slate-200"
    >
      <div
        class="flex flex-row justify-start items-center gap-x-3 pb-2 border-b border-slate-100"
      >
        <mat-icon class="text-emerald-500">payments</mat-icon>
        <h2 class="text-lg font-bold text-slate-800">Pricing</h2>
      </div>

      <div class="flex flex-col gap-2">
        <ng-container *ngIf="type === 'standard'" ; else sequential>
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ng-container *ngFor="let price of _standardPrices">
              <div
                class="flex flex-col p-2 rounded-md hover:bg-emerald-50 transition-colors"
              >
                <span class="text-sm font-medium text-slate-600">{{
                  price.label
                }}</span>
                <div class="flex flex-wrap gap-x-2">
                  <span
                    *ngFor="let value of price.values; let last = last"
                    class="text-lg font-semibold text-emerald-700"
                  >
                    {{ value.value | hlCurrency: value.currency | async }}
                    <span *ngIf="!last" class="text-slate-400">/</span>
                  </span>
                </div>
              </div>
            </ng-container>
          </div>
        </ng-container>

        <ng-template #sequential>
          <div class="grid grid-cols-2 gap-3">
            <ng-container *ngFor="let price of _sequentialPrices">
              <div
                class="flex flex-col p-2 rounded-md hover:bg-emerald-50 transition-colors"
              >
                <span class="text-sm font-medium text-slate-600">{{
                  price.label
                }}</span>
                <span class="text-lg font-semibold text-emerald-700">
                  {{ price.value | currency: price.currency }}
                </span>
              </div>
            </ng-container>
          </div>
        </ng-template>
      </div>

      <div
        *ngIf="!_prices || _prices.length === 0"
        class="flex items-center justify-center py-6"
      >
        <p class="text-slate-500 italic flex items-center gap-2">
          <mat-icon class="text-slate-400">info</mat-icon>
          No pricing information available
        </p>
      </div>
    </div>
  `,
  providers: [OrdinalPipe],
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe, CurrencyPipe, HlCurrencyPipe, MatIconModule]
})
export class PricesDisplayComponent {
  @Input() set prices(value: Price[]) {
    if (value && value.length) {
      this._prices = value;
      this.type = value[0].type;
      this.buildPrices();
    } else {
      this._prices = [];
    }
  }

  _sequentialPrices: { label: string; value: number; currency: string }[] = [];
  _standardPrices: {
    label: string;
    key: string;
    values: { value: number; currency: string }[];
  }[] = [];

  type: PRICE_TYPE = PRICE_TYPE.STANDARD;

  _prices: Price[] = [];

  constructor(private ordinalPipe: OrdinalPipe) {}

  private buildPrices() {
    if (!this._prices) {
      return;
    }
    if (this.type === PRICE_TYPE.STANDARD) {
      this.setStandardPrices(
        this._prices.filter((price) => price.type === PRICE_TYPE.STANDARD)
      );
    }
    if (this.type === PRICE_TYPE.SEQUENTIAL) {
      this.setSequentialPrices(
        this._prices.filter((price) => price.type === PRICE_TYPE.SEQUENTIAL)
      );
    }
  }

  private setStandardPrices(prices: Price[]) {
    const pricesGroupedByKey = prices
      .map(({ unit, sequence, ...others }) => ({
        ...others,
        unit,
        sequence,
        key: `${unit}${sequence ? sequence : ''}`
      }))
      .reduce(
        (acc, { key, value, currency_code }) => {
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push({ value, currency: currency_code ?? null });
          return acc;
        },
        {} as { [key: string]: { value: number; currency: string }[] }
      );
    const labelsMap = {
      day: 'First day',
      'day*': 'Add. day',
      week: 'Week',
      weekend: 'Weekend',
      month: 'Month'
    };
    const keyOrder = ['day', 'day*', 'week', 'weekend', 'month'];
    this._standardPrices = Object.entries(pricesGroupedByKey)
      .map(([key, values]) => ({
        key,
        label: labelsMap[key],
        values
      }))
      .sort((a, b) => keyOrder.indexOf(a.key) - keyOrder.indexOf(b.key));
  }

  private fillDefaultCurrencyAndSortByCurrency(prices: Price[]): Price[] {
    const currencies = Array.from(
      new Set(prices.map((price) => price.currency_code).filter(Boolean))
    );
    return prices
      .map(({ currency_code, ...others }) => ({
        currency_code: currency_code ?? null,
        ...others
      }))
      .sort(
        ({ currency_code: a }, { currency_code: b }) =>
          currencies.indexOf(a) - currencies.indexOf(b)
      );
  }

  private setSequentialPrices(prices: Price[]) {
    prices = this.fillDefaultCurrencyAndSortByCurrency(prices);
    const additionals = prices.filter((price) => price.sequence === '*');
    const sequentials = prices
      .filter((price) => price.sequence !== '*')
      .sort(
        (priceA, priceB) =>
          parseInt(priceA.sequence, 10) - parseInt(priceB.sequence, 10)
      );
    this._sequentialPrices = sequentials.map((price) => {
      let label = this.ordinalPipe.transform(parseInt(price.sequence, 10) + 1);
      const usePlural = price.multiplier > 1;
      label += usePlural
        ? ` ${price.multiplier} ${price.unit}s`
        : ` ${price.unit}`;
      return { label, value: price.value, currency: price.currency_code };
    });
    if (additionals.length) {
      const usePlural = additionals[0].multiplier > 1;
      let label = `Each additional`;
      label += usePlural
        ? `${additionals[0].multiplier} ${additionals[0].unit}s`
        : ` ${additionals[0].unit}`;
      this._sequentialPrices.push({
        label,
        value: additionals[0].value,
        currency: additionals[0].currency_code
      });
    }
  }
}
